/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import DS from 'ember-data';
import { computed, action } from '@ember/object';
import { and, mapBy, map } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import AuthService from 'cio/services/auth';
import EnvironmentService from 'cio/services/environment';
import FacebookLeadAdsService, { FacebookBusinessPageData } from 'cio/services/facebook-lead-ads';
import CustomFormsService from 'cio/services/custom-forms';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import CampaignModel, {
  FormTriggeredCampaignAttributeFilters,
  FormTriggeredCampaignFormTriggerFilterChildren,
} from 'cio/models/campaign';
import GroupCondition from 'cio/app-models/conditions/group';
import FormEventAttributeCondition from 'cio/app-models/conditions/form-event-attribute';
import { tracked } from '@glimmer/tracking';
import AlertsService from 'cio/services/alerts';
import ApiErrorsService from 'cio/services/api-errors';
import EventAttributeCondition from 'cio/app-models/conditions/event-attribute';
import CustomForm from 'cio/models/custom-form';
import { all } from 'rsvp';

interface FormArgs {
  campaign: CampaignModel;
  filters: unknown;
  restartMinInterval: number;
  attributeFilters: FormTriggeredCampaignAttributeFilters;
  isDuplicatingForm?: boolean;
}

export default class Form extends Component<FormArgs> {
  @service auth!: AuthService;
  @service alerts!: AlertsService;
  @service apiErrors!: ApiErrorsService;
  @service store!: DS.Store;
  @service environment!: EnvironmentService;
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;
  @tracked showRequestFormProviderModal = false;
  @tracked connectingNewForm = false;

  @mapBy('args.attributeFilters.children', 'children')
  formTriggerConditionSets!: FormTriggeredCampaignFormTriggerFilterChildren;
  @mapBy('formTriggerConditionSets', 'firstObject') formConditions!: FormEventAttributeCondition[];
  @map('formTriggerConditionSets', function (c: FormEventAttributeCondition[] | GroupCondition[]) {
    return c.length > 1 ? c[1] : null;
  })
  eventDataFiltersConditions!: (GroupCondition | null)[];

  @computed('formConditions.@each.value', 'eventDataFiltersConditions.[]')
  get selectedForms() {
    return this.formConditions.map((formCondition, index) => {
      let dataFiltersCondition = this.eventDataFiltersConditions[index];
      let formId = formCondition.value;
      let form: CustomForm | FacebookLeadAdsForm | undefined =
        this.facebookLeadAds.forms.findBy('facebookFormId', formId) ??
        this.customForms.forms.find((f) => f.formIdLowercase === formId);
      return {
        form,
        eventDataFilters: dataFiltersCondition ?? null,
      };
    });
  }

  // relevant for enabling the integration (connecting to Facebook Business Page):
  @tracked userAccessToken: string | null = null;
  @tracked facebookAccountName: string | null = null;
  @tracked pages: FacebookBusinessPageData[] | null = null;

  get selectedFormsForms() {
    let forms = this.selectedForms.mapBy('form').filter(Boolean);
    return forms.length > 0 ? forms : null;
  }

  get hasMoreFormsToSelect() {
    return this.selectedForms.length < this.selectableForms.length;
  }

  @computed('facebookLeadAds.connectedForms.[]', 'customForms.connectedOrDraftForms.[]')
  get selectableForms(): (CustomForm | FacebookLeadAdsForm)[] {
    return [...this.facebookLeadAds.connectedForms, ...this.customForms.connectedOrDraftForms];
  }

  constructor(owner: unknown, args: FormArgs) {
    super(owner, args);

    this.initAllTheValues();
  }

  @and('facebookLeadAds.initialized', 'customForms.initialized') formsInitialized!: boolean;

  async initAllTheValues() {
    await all([this.facebookLeadAds.initialize(), this.customForms.initialize()]);

    if (!this.selectableForms.length) {
      this.connectingNewForm = true;
    }

    // always have at least one forms dropdown if there are selectable forms:
    if (!this.selectedForms.length && this.selectableForms.length) {
      this.addForm();
    }
  }

  @action addForm(form?: CustomForm | FacebookLeadAdsForm) {
    this.args.attributeFilters.set('children', [
      ...this.args.attributeFilters.children,
      GroupCondition.create({
        operator: 'and',
        children: [
          FormEventAttributeCondition.create({
            formType: form?.formType,
            shouldValidate: true,
          }),
        ],
      }),
    ]);
  }

  @action selectForm(index: number, f: CustomForm | FacebookLeadAdsForm) {
    let selectedForms = [...this.selectedForms];
    let form = selectedForms[index]?.form;
    if (form?.formIdLowercase !== f.formId) {
      let { formIdLowercase: value, formType } = f;
      this.args.attributeFilters.children[index].children[0].setProperties({
        value,
        formType,
      });
      return;
    }
    // selecting the same form as previously selected: no need to do anything
  }

  @action removeForm(index: number) {
    this.args.attributeFilters.set(
      'children',
      this.args.attributeFilters.children.filter((_v, i) => i !== index),
    );
  }

  @action addFilter(index: number) {
    let newFilter = EventAttributeCondition.create({
      event: 'form_submit',
      name: '',
      operator: 'eq',
      value: '',
      shouldValidate: false,
    });

    let eventDataFilters = this.selectedForms[index]?.eventDataFilters;
    if (!eventDataFilters) {
      eventDataFilters = GroupCondition.create({
        operator: 'and',
        children: [newFilter],
      });

      this.args.attributeFilters.children[index].set('children', [
        ...this.args.attributeFilters.children[index].children,
        eventDataFilters,
      ]);
    } else {
      eventDataFilters.set('children', [...eventDataFilters.children, newFilter]);
    }
  }

  @action removeFilter(index: number, filter: EventAttributeCondition) {
    let eventDataFilters = this.selectedForms[index]?.eventDataFilters as GroupCondition;
    eventDataFilters.set(
      'children',
      eventDataFilters.children.filter((f) => f !== filter),
    );

    // last filter removed: remove whole event data filters condition
    if (!eventDataFilters.children.length) {
      this.args.attributeFilters.children[index].set(
        'children',
        this.args.attributeFilters.children[index].children.filter((c) => c !== eventDataFilters),
      );
    }
  }

  private setForm(form: FacebookLeadAdsForm | CustomForm) {
    // create a new form row if either:
    // - there are no form rows, because first form is being connected
    // - last form row already has a selected form
    if (!this.selectedForms.length || this.selectedForms.lastObject?.form) {
      this.addForm(form);
    }
    this.selectForm(this.selectedForms.length - 1, form);
  }

  @action
  async onConnectForm(form: FacebookLeadAdsForm | CustomForm) {
    this.setForm(form);
    this.connectingNewForm = false;
  }

  @action
  addNewFormAction() {
    this.connectingNewForm = true;
  }
}
